import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { winApi, skillApi } from '@/services/skillKnowledgeApi';
import { useEntity, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { LoadingState, ErrorState } from '@/components/common';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import type { Win, Skill, SkillLevel } from '@/types/skillKnowledge';

const SKILL_LEVELS: SkillLevel[] = ['FOUNDATIONAL', 'BASIC', 'INTERMEDIATE', 'ADVANCED', 'EXPERT', 'MASTERY'];

const winRequiredSkillSchema = z.object({
  skillId: z.string().min(1, 'Skill ID is required'),
  proficiencyLevel: z.enum(['FOUNDATIONAL', 'BASIC', 'INTERMEDIATE', 'ADVANCED', 'EXPERT', 'MASTERY']),
});

const winSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(200, 'Name must be less than 200 characters'),
  description: z.string().min(10, 'Description must be at least 10 characters').max(2000, 'Description must be less than 2000 characters'),
  summary: z.string().max(500, 'Summary must be less than 500 characters').optional(),
  requiredSkills: z.array(winRequiredSkillSchema).min(1, 'At least one required skill is needed'),
  relatedPainPointIds: z.array(z.string()).optional(),
  isActive: z.boolean(),
});

type WinFormData = z.infer<typeof winSchema>;

export const WinFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [skills, setSkills] = useState<Skill[]>([]);

  const form = useForm<WinFormData>({
    resolver: zodResolver(winSchema),
    defaultValues: {
      name: '',
      description: '',
      summary: '',
      requiredSkills: [],
      relatedPainPointIds: [],
      isActive: true,
    },
  });

  const { fields: requiredSkillFields, append: appendRequiredSkill, remove: removeRequiredSkill } = useFieldArray({
    control: form.control,
    name: 'requiredSkills',
  });

  useEffect(() => {
    // Load skills for dropdown
    skillApi.list({ page: 0, size: 1000 }).then((response) => {
      setSkills(response.content || []);
    }).catch((error) => {
      console.error('Error loading skills:', error);
    });
  }, []);

  // Fetch win data for edit mode
  const {
    data: _win,
    isLoading: isLoadingWin,
    error: winError,
  } = useEntity<Win>({
    fetchFn: (winId: string) => winApi.getById(winId),
    entityId: id,
    entityName: 'win',
    enabled: isEdit && !!id,
    onSuccess: (data) => {
      form.reset({
        name: data.name,
        description: data.description,
        summary: data.summary || '',
        requiredSkills: data.requiredSkills || [],
        relatedPainPointIds: data.relatedPainPointIds || [],
        isActive: data.isActive ?? true,
      });
    },
    onError: () => {
      navigate('/admin/wins');
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: WinFormData) => {
      const payload: Partial<Win> = {
        name: data.name,
        description: data.description,
        summary: data.summary,
        requiredSkills: data.requiredSkills,
        relatedPainPointIds: data.relatedPainPointIds?.filter(Boolean) || [],
        isActive: data.isActive,
      };
      return await winApi.create(payload as Omit<Win, 'id' | 'createdAt' | 'updatedAt'>);
    },
    entityName: 'win',
    operation: 'create',
    onSuccess: () => {
      navigate('/admin/wins');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: WinFormData) => {
      if (!id) throw new Error('Win ID is required');
      const payload: Partial<Win> = {
        name: data.name,
        description: data.description,
        summary: data.summary,
        requiredSkills: data.requiredSkills,
        relatedPainPointIds: data.relatedPainPointIds?.filter(Boolean) || [],
        isActive: data.isActive,
      };
      return await winApi.update(id, payload);
    },
    entityName: 'win',
    operation: 'update',
    onSuccess: () => {
      navigate('/admin/wins');
    },
  });

  const onSubmit = async (data: WinFormData) => {
    if (isEdit) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const isLoading = isLoadingWin;
  const mutation = isEdit ? updateMutation : createMutation;

  // In edit mode, show loading state while fetching
  if (isEdit && isLoading) {
    return <LoadingState message="Loading win..." />;
  }

  // Show error if there's an error (only after loading completes)
  if (isEdit && winError) {
    return <ErrorState error={winError} onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/wins">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEdit ? 'Edit Win' : 'Create Win'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {isEdit ? 'Update win information' : 'Create a new win (achievement/milestone)'}
            </p>
          </div>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, (errors) => {
          console.error('Form validation errors:', errors);
        })} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Enter the basic details for the win</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter win name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description *</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Enter win description"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="summary"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Summary</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter win summary" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Active</FormLabel>
                      <FormDescription>Mark this win as active</FormDescription>
                    </div>
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="h-4 w-4"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Required Skills</CardTitle>
              <CardDescription>Define the skills required to achieve this win</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {requiredSkillFields.map((field, index) => (
                <div key={field.id} className="grid gap-4 md:grid-cols-[1fr_auto_auto] items-end">
                  <FormField
                    control={form.control}
                    name={`requiredSkills.${index}.skillId`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Skill</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select skill" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {skills.map((skill) => (
                              <SelectItem key={skill.id} value={skill.id || ''}>
                                {skill.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={`requiredSkills.${index}.proficiencyLevel`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Level</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Level" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {SKILL_LEVELS.map((level) => (
                              <SelectItem key={level} value={level}>
                                {level.replace(/_/g, ' ')}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeRequiredSkill(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={() => appendRequiredSkill({ skillId: '', proficiencyLevel: 'BASIC' })}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Required Skill
              </Button>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" asChild>
              <Link to="/admin/wins">Cancel</Link>
            </Button>
            <Button type="submit" disabled={mutation.isLoading}>
              <Save className="mr-2 h-4 w-4" />
              {mutation.isLoading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default WinFormPage;
